import React from "react";
import { Bell, Eye, Pencil, PinOff, Plus, Trash2, Upload, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Separator } from "@/components/ui/separator";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  type AdminAnnouncement,
  type AccountTier,
  type AnnouncementAudience,
  type AnnouncementSeverity,
} from "@/lib/announcements";
import { tierDisplayName } from "@/lib/tier";
import { TIERS, type FormState, fromLocalInput } from "./announcementHelpers";

interface Props {
  editingId: string | null;
  form: FormState;
  setForm: React.Dispatch<React.SetStateAction<FormState>>;
  saving: boolean;
  previewVideo: { kind: "iframe" | "video"; src: string } | null;
  items: AdminAnnouncement[];
  userId: string;
  onSave: (publish: boolean) => void;
  onTogglePublish: (a: AdminAnnouncement) => void;
  onDelete: (id: string) => void;
  onPreview: (a: AdminAnnouncement) => void;
  onStartNew: () => void;
}

export const AnnouncementEditorCard: React.FC<Props> = ({
  editingId,
  form,
  setForm,
  saving,
  previewVideo,
  items,
  userId,
  onSave,
  onTogglePublish,
  onDelete,
  onPreview,
  onStartNew,
}) => {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base flex items-center gap-2">
          {editingId ? <Pencil className="h-4 w-4" /> : <Plus className="h-4 w-4" />}
          {editingId ? "Edit announcement" : "New announcement"}
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div className="space-y-1.5">
          <div className="flex items-center justify-between">
            <Label htmlFor="ann-title">Title</Label>
            <span className={`text-xs ${form.title.length > 80 ? "text-destructive" : "text-muted-foreground"}`}>
              {form.title.length}/80
            </span>
          </div>
          <Input
            id="ann-title"
            value={form.title}
            onChange={(e) => setForm({ ...form, title: e.target.value })}
            placeholder="What's new"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="ann-body">Message (markdown supported)</Label>
          <Textarea
            id="ann-body"
            rows={8}
            value={form.body_md}
            onChange={(e) => setForm({ ...form, body_md: e.target.value })}
            placeholder="Tell users what's new, share an update, or link to a video…"
          />
        </div>

        <div className="space-y-1.5">
          <Label htmlFor="ann-video">Video URL (optional)</Label>
          <Input
            id="ann-video"
            value={form.video_url}
            onChange={(e) => setForm({ ...form, video_url: e.target.value })}
            placeholder="https://www.youtube.com/watch?v=… or .mp4"
          />
          {previewVideo && (
            <div className="mt-2 aspect-video w-full overflow-hidden rounded-md border bg-muted">
              {previewVideo.kind === "iframe" ? (
                <iframe
                  src={previewVideo.src}
                  className="h-full w-full"
                  allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                  allowFullScreen
                  title="Video preview"
                />
              ) : (
                <video src={previewVideo.src} controls className="h-full w-full" />
              )}
            </div>
          )}
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label>Severity</Label>
            <Select
              value={form.severity}
              onValueChange={(v) => setForm({ ...form, severity: v as AnnouncementSeverity })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="info">Info</SelectItem>
                <SelectItem value="success">Success</SelectItem>
                <SelectItem value="warning">Warning (shows banner)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div className="space-y-1.5">
            <Label>Audience</Label>
            <Select
              value={form.audience}
              onValueChange={(v) => setForm({ ...form, audience: v as AnnouncementAudience })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All users</SelectItem>
                <SelectItem value="tier">Specific tier</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {form.audience === "tier" && (
          <div className="space-y-1.5">
            <Label>Tier</Label>
            <Select
              value={form.audience_tier}
              onValueChange={(v) => setForm({ ...form, audience_tier: v as AccountTier })}
            >
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {TIERS.map((t) => (
                  <SelectItem key={t} value={t}>{tierDisplayName(t)}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}

        <Separator />

        <div className="flex items-center justify-between gap-3">
          <div className="space-y-0.5">
            <Label htmlFor="ann-pin">Pin to top + show banner</Label>
            <p className="text-xs text-muted-foreground">Pinned items appear first and trigger a top banner.</p>
          </div>
          <Switch id="ann-pin" checked={form.is_pinned} onCheckedChange={(v) => setForm({ ...form, is_pinned: v })} />
        </div>

        <div className="flex items-center justify-between gap-3">
          <div className="space-y-0.5">
            <Label htmlFor="ann-now">Publish immediately on save</Label>
            <p className="text-xs text-muted-foreground">If off, set a scheduled publish time below.</p>
          </div>
          <Switch id="ann-now" checked={form.publish_now} onCheckedChange={(v) => setForm({ ...form, publish_now: v })} />
        </div>

        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="ann-sched">Scheduled publish (optional)</Label>
            <Input
              id="ann-sched"
              type="datetime-local"
              value={form.scheduled_at}
              disabled={form.publish_now}
              onChange={(e) => setForm({ ...form, scheduled_at: e.target.value })}
            />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="ann-exp">Expires at (optional)</Label>
            <Input
              id="ann-exp"
              type="datetime-local"
              value={form.expires_at}
              onChange={(e) => setForm({ ...form, expires_at: e.target.value })}
            />
          </div>
        </div>

        <Separator />

        <div className="flex flex-wrap gap-2">
          <Button onClick={() => onSave(false)} disabled={saving} variant="outline">
            <Pencil className="h-4 w-4" /> Save draft
          </Button>
          <Button onClick={() => onSave(true)} disabled={saving}>
            <Upload className="h-4 w-4" /> Save & publish
          </Button>
          <Button
            variant="outline"
            onClick={() =>
              onPreview({
                id: editingId ?? "preview",
                created_by_user_id: userId,
                title: form.title || "(untitled)",
                body_md: form.body_md,
                video_url: form.video_url || null,
                severity: form.severity,
                audience: form.audience,
                audience_tier: form.audience === "tier" ? form.audience_tier : null,
                published_at: form.publish_now
                  ? new Date().toISOString()
                  : fromLocalInput(form.scheduled_at),
                expires_at: fromLocalInput(form.expires_at),
                is_pinned: form.is_pinned,
                created_at: new Date().toISOString(),
                updated_at: new Date().toISOString(),
              })
            }
          >
            <Eye className="h-4 w-4" /> Preview as user
          </Button>
          {editingId && (
            <>
              <Button
                variant="outline"
                onClick={() => {
                  const a = items.find((i) => i.id === editingId);
                  if (a) onTogglePublish(a);
                }}
              >
                {items.find((i) => i.id === editingId)?.published_at ? (
                  <><PinOff className="h-4 w-4" /> Unpublish</>
                ) : (
                  <><Bell className="h-4 w-4" /> Publish now</>
                )}
              </Button>
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="destructive" size="sm" className="ml-auto">
                    <Trash2 className="h-4 w-4" /> Delete
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Delete this announcement?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This permanently removes the announcement and all read receipts. This cannot be undone.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={() => editingId && onDelete(editingId)}>Delete</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            </>
          )}
          {editingId && (
            <Button variant="ghost" size="sm" onClick={onStartNew}>
              <X className="h-4 w-4" /> Cancel
            </Button>
          )}
        </div>
      </CardContent>
    </Card>
  );
};
